import { CommunityStories, Story, Community } from "../../types";
import React, { useCallback, useMemo, useEffect } from "react";
import { useRouter } from "next/router";
import useSWRInfinite, { SWRInfiniteKeyLoader } from "swr/infinite";
import { Spin, Typography } from "@dreambigger/design-system";
import StoryCard from "./feed-cards/story-card";
import Masonry from "react-masonry-css";
import styles from "@dreambigger/shared/src/components/board/styles/react-masonry.module.scss";
import InfiniteScroll from "react-infinite-scroll-component";
import useUser from "../../hooks/use-user";
import { api, tokenHelper } from "../../api/weserve/api-helper";
import { useGA, useMixpanel, useAppcues, useSegment } from "../../hooks";
import { boardFetcherFn, MASONRY_DEFAULTS_COLS } from "../../types/board";
import InspireAnalytics, {
  InspireAnalyticsHelper,
  InspireAnalyticEvents,
} from "@dreambigger/shared/src/utils/inspire-analytics-helper";

const { Title } = Typography;

interface StoryBoardProps {
  community: Community;
  showStoryComments: boolean;
  boardTitle: string | React.ReactNode;
  getUrlKey: SWRInfiniteKeyLoader;
  fixedFirstCard?: JSX.Element;
  initialData?: CommunityStories[];
  disableAnalytics?: boolean;
}

const StoryBoard: React.FC<StoryBoardProps> = ({
  community,
  fixedFirstCard,
  boardTitle,
  showStoryComments,
  getUrlKey,
  initialData: initialStories,
  disableAnalytics,
}) => {
  const [userLoading, { data: me }] = useUser(api, tokenHelper);
  const router = useRouter();

  // Load analytics hooks
  const mixpanel = useMixpanel();
  const segment = useSegment();
  const googleAnalytics = useGA();

  // Load appcues hook
  const appcues = useAppcues();

  const { data, setSize: setPageIndex } = useSWRInfinite<CommunityStories>(
    getUrlKey,
    boardFetcherFn,
    {
      revalidateFirstPage: false,
    }
  );

  // Track the page view for analytics and appcues.
  useEffect(() => {
    // Give GoogleAnalytics time to initialize
    setTimeout(() => {
      // Wait for a response on whether a user is logged in.
      if (userLoading) {
        return;
      }
      // Disable metric tracking if component is reused in a place where we don't want it tracked (like on the event detail page, which has its own appcues call)
      if (disableAnalytics) {
        return;
      }

      googleAnalytics.setDefaultDimensions(community.slug, me);
      googleAnalytics.pageview(window.location.pathname);
      InspireAnalytics.track({
        eventName: InspireAnalyticEvents.PAGE_VIEW,
        properties: {
          Page: window.location.pathname,
          "Page Type": InspireAnalyticsHelper.getPageType(router.route),
          "Host Name": window.location.host,
        },
        mixpanel,
        segment,
      });
      // If there is a user, track user page visit on appcues
      if (me?.id) {
        appcues.identify(me.id.toString());
      }
    }, 500);
  }, [userLoading]);

  const storyData = data || initialStories;

  const feed = useMemo(() => {
    if (!storyData) {
      return {
        records: [],
        totalRecords: 0,
      };
    }

    const totalRecords: number = storyData?.[0]?.totalRecords || 0;

    // useSWRInfinite returns data in an array of record arrays, i.e. =>
    // [{records: [{id: 1, ...}, {id: 2, ...}], totalRecords: 18}, {records: [{id: 2, ...}, {id: 3, ...}], totalRecords: 18}]
    // We need to flatten these records to create 1 array of records.
    const records = storyData.reduce(
      (previousValue, currentData) => [
        ...previousValue,
        ...(currentData?.records || []),
      ],
      [] as Story[]
    );

    return {
      records,
      totalRecords,
    };
  }, [storyData]);

  const cards = useMemo(
    () =>
      feed.records.map((story) => (
        <StoryCard
          key={story.id}
          feedItem={story}
          currentUser={me}
          userLoading={userLoading}
          showComments={showStoryComments}
        />
      )),
    [feed, userLoading, showStoryComments, me]
  );

  const loadNextPage = useCallback(() => {
    setPageIndex((val) => {
      googleAnalytics.event({
        category: `Feed Pagination`,
        action: `Loaded page ${val + 1}`,
        label: "Infinite Scroll",
      });
      return val + 1;
    });
  }, []);

  const isEmpty = feed.records.length === 0;
  const hasMoreRecords =
    !isEmpty && feed.records && feed.totalRecords > feed.records.length;

  return (
    <>
      {typeof boardTitle === "string" ? (
        <Title>{boardTitle}</Title>
      ) : (
        boardTitle
      )}
      <div className="pt-5">
        <InfiniteScroll
          dataLength={cards.length}
          next={loadNextPage}
          hasMore={hasMoreRecords}
          loader={
            <Spin
              size="large"
              className="w-100 mv-9 primary"
              tip="Loading More..."
            />
          }
          className="overflow-visible"
        >
          <Masonry
            breakpointCols={MASONRY_DEFAULTS_COLS}
            className={styles.grid}
            columnClassName={styles.column}
          >
            {fixedFirstCard && (
              <div style={{ marginBottom: 30 }}>{fixedFirstCard}</div>
            )}
            {cards}
          </Masonry>
        </InfiniteScroll>
      </div>
    </>
  );
};

export default StoryBoard;
